// frontend/src/components/InlineBanner.js

import React from "react";
import { Box, Typography } from "@mui/material";
import { ArrowRight, ChevronRight } from "lucide-react";
import { colors, typography, transitions } from "../theme/tokens";

/**
 * InlineBanner
 *
 * Shared premium "setup / status prompt" card used by dashboard-level
 * banners (MissingCategoriesBanner, SubscriptionBanner).
 *
 * Design intent (Step 4/5 UI refresh):
 *   - Width constrained to 640px and centered — never a page-wide strip.
 *   - Layered shadow for real card elevation off the neutral background.
 *   - The entire content row is a single <button>. One click target, one
 *     focus ring. Avoids nested-button HTML.
 *   - Responsive affordance:
 *       · mobile (xs)  — compact single row, chevron-only end glyph
 *                        (Linear / iOS Settings style).
 *       · desktop (sm) — labeled gradient pill at the end (decorative
 *                        <span>; clicks bubble to the wrapper button).
 *   - Severity conveyed via a 3px accent bar + a tinted icon badge.
 *
 * Consumers pre-localize all text strings — this component is writing-
 * direction aware only for arrow/chevron mirroring and text alignment.
 *
 * Props:
 *   severity     "attention" | "info" | "success" | "error"
 *                Maps to a brand-tuned accent color (see ACCENT_HEX).
 *   icon         Lucide icon *component* reference (not a rendered element).
 *   title        Pre-localized string for the main line.
 *   body         Pre-localized string for the secondary line. Optional.
 *   actionLabel  Pre-localized label for the desktop CTA pill AND the
 *                accessible name of the underlying <button>.
 *   onAction     Click/tap handler.
 *   isHebrew     Controls writing-direction-dependent behavior (chevron
 *                mirroring, text alignment).
 */

// ─── Severity → accent color mapping ──────────────────────────────────────
// Coral salmon is the natural complement of turquoise on the color wheel
// (Stripe, Linear use variants) — warm "needs attention" tone that sits
// next to the brand's turquoise CTA without clashing. Muted teal-blue
// handles informational states. Brand turquoise handles success. Muted
// brick replaces emergency-red for errors.
export const ACCENT_HEX = {
  attention: "#E88A73",            // dusty salmon
  info:      "#4A8FA8",            // muted teal-blue
  success:   colors.primary.main,  // brand turquoise
  error:     "#A85D5D",            // muted brick
};

// 12% alpha hex suffix for the soft icon-badge background tint.
const TINT_ALPHA = "1F";

// Zero-out browser default <button> chrome without losing our box styling.
const BUTTON_RESET = {
  background:    "transparent",
  border:        "none",
  padding:       0,
  margin:        0,
  font:          "inherit",
  color:         "inherit",
  textAlign:     "inherit",
  width:         "100%",
  cursor:        "pointer",
  display:       "flex",
  alignItems:    "center",
};

const InlineBanner = ({
  severity = "attention",
  icon: IconComponent,
  title,
  body,
  actionLabel,
  onAction,
  isHebrew = false,
}) => {
  const accent = ACCENT_HEX[severity] ?? ACCENT_HEX.attention;

  // When the accent is the brand primary, prefer the token-sourced alpha
  // variant; for custom hex accents, synthesise a 12% tint.
  const tint =
    accent === colors.primary.main
      ? colors.primary.alpha12
      : `${accent}${TINT_ALPHA}`;

  return (
    <Box sx={{ mb: 3, display: "flex", justifyContent: "center" }}>
      <Box
        role="status"
        aria-live="polite"
        sx={{
          width: "100%",
          maxWidth: 640,
          display: "flex",
          alignItems: "stretch",
          backgroundColor: colors.background.default,
          border: `1px solid ${colors.primary.alpha08}`,
          borderRadius: "16px",
          boxShadow:
            "0 4px 20px rgba(20, 30, 45, 0.06), 0 1px 3px rgba(20, 30, 45, 0.04)",
          overflow: "hidden",
          transition: `box-shadow ${transitions.base}`,
          "&:hover": {
            boxShadow:
              "0 8px 28px rgba(20, 30, 45, 0.08), 0 1px 3px rgba(20, 30, 45, 0.04)",
          },
        }}
      >
        {/* 3px severity accent bar — logical-start side */}
        <Box
          aria-hidden="true"
          sx={{
            width: "3px",
            flexShrink: 0,
            backgroundColor: accent,
          }}
        />

        {/* Content row — IS the button. One click target, one focus ring. */}
        <Box
          component="button"
          type="button"
          onClick={onAction}
          aria-label={actionLabel}
          sx={{
            ...BUTTON_RESET,
            gap: { xs: 1.25, sm: 1.75 },
            px: { xs: 1.5, sm: 2.5 },
            py: { xs: 1.5, sm: 1.75 },
            // Drive the decorative CTA pill + chevron off the wrapper's
            // hover/active so they behave as if they were a real button.
            "&:hover .cta-pill": {
              background: "linear-gradient(135deg, #48D1CC 0%, #00B5A2 100%)",
              boxShadow: "0 4px 16px rgba(0, 168, 150, 0.38)",
              transform: "translateY(-1px)",
            },
            "&:hover .cta-chevron": {
              transform: isHebrew ? "translateX(-2px)" : "translateX(2px)",
              color: colors.primary.main,
            },
            "&:active .cta-pill": { transform: "translateY(0)" },
            "&:focus-visible": {
              outline:       `2px solid ${colors.primary.main}`,
              outlineOffset: 2,
              borderRadius:  "12px",
            },
          }}
        >
          {/* Icon badge */}
          <Box
            sx={{
              width:           { xs: 34, sm: 40 },
              height:          { xs: 34, sm: 40 },
              borderRadius:    { xs: "10px", sm: "12px" },
              backgroundColor: tint,
              display:         "flex",
              alignItems:      "center",
              justifyContent:  "center",
              flexShrink:      0,
            }}
          >
            <IconComponent
              size={18}
              strokeWidth={1.75}
              color={accent}
              aria-hidden="true"
            />
          </Box>

          {/* Text block */}
          <Box
            sx={{
              minWidth:  0,
              flex:      1,
              textAlign: isHebrew ? "right" : "left",
            }}
          >
            <Typography
              sx={{
                fontSize:      { xs: "13px", sm: "14.5px" },
                fontWeight:    600,
                color:         colors.text.heading,
                fontFamily:    typography.fontFamily.body,
                lineHeight:    1.35,
                letterSpacing: "-0.005em",
              }}
            >
              {title}
            </Typography>
            {body && (
              <Typography
                sx={{
                  fontSize:     { xs: "11.75px", sm: "12.5px" },
                  color:        colors.text.label,
                  fontFamily:   typography.fontFamily.body,
                  lineHeight:   1.5,
                  mt:           0.25,
                  // Truncate gracefully on very narrow mobile viewports so
                  // the chevron end-cap never wraps onto a second line.
                  overflow:     "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace:   { xs: "nowrap", sm: "normal" },
                }}
              >
                {body}
              </Typography>
            )}
          </Box>

          {/* Mobile affordance: chevron only — iOS Settings / Linear feel */}
          <Box
            className="cta-chevron"
            aria-hidden="true"
            sx={{
              display:        { xs: "inline-flex", sm: "none" },
              alignItems:     "center",
              justifyContent: "center",
              color:          colors.text.label,
              flexShrink:     0,
              transition:     `transform ${transitions.fast}, color ${transitions.fast}`,
              transform:      isHebrew ? "scaleX(-1)" : "none",
            }}
          >
            <ChevronRight size={20} strokeWidth={2} />
          </Box>

          {/* Desktop affordance: labeled gradient pill — decorative <span>,
              clicks bubble to the wrapper button. */}
          <Box
            className="cta-pill"
            component="span"
            aria-hidden="true"
            sx={{
              display:       { xs: "none", sm: "inline-flex" },
              alignItems:    "center",
              gap:           0.75,
              flexShrink:    0,
              height:        36,
              px:            2.25,
              borderRadius:  "10px",
              fontSize:      "13.5px",
              fontWeight:    600,
              fontFamily:    typography.fontFamily.body,
              letterSpacing: "0.005em",
              color:         "#ffffff",
              background:    `linear-gradient(135deg, ${colors.primary.main} 0%, #00A896 100%)`,
              boxShadow:     "0 2px 10px rgba(0, 168, 150, 0.28)",
              whiteSpace:    "nowrap",
              transition:    "all 0.2s ease",
            }}
          >
            <span>{actionLabel}</span>
            <Box
              component="span"
              sx={{
                display:   "inline-flex",
                transform: isHebrew ? "scaleX(-1)" : "none",
              }}
            >
              <ArrowRight size={14} strokeWidth={2.25} />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default InlineBanner;
